"use client";

import { useState, useEffect } from 'react';
import useUserStore from '@/store/userStore';
import { 
  hasConsent, 
  getPreferences,
  hasUserMadeChoice,
  updatePreferences,
  acceptAll
} from '@/utils/cookieConsentUtil';
import { updateConsentState } from '@/utils/gtmConsent';

/**
 * Функция для безопасной сериализации объектов с циклическими ссылками
 */
function safeStringify(obj, indent = 2) {
  const cache = new Set();
  
  const safeObj = JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      // Проверяем на циклические ссылки
      if (cache.has(value)) {
        return '[Circular Reference]';
      }
      cache.add(value);
    }
    
    // Пропускаем DOM-элементы и React-компоненты
    if (value && (
      (typeof value === 'object' && value.nodeType) || 
      key.startsWith('__react') || 
      key === 'stateNode' ||
      key === '_owner'
    )) {
      return '[Object]';
    }
    
    return value;
  }, indent);
  
  return safeObj;
}

/**
 * Упрощает объект события dataLayer для отображения
 */
function simplifyDataLayerEvent(event) {
  if (!event || typeof event !== 'object') return { type: 'unknown' };
  
  try {
    // Создаем безопасную копию с только интересующими нас полями
    const simplified = {};
    
    // Добавляем event если есть
    if (event.event) simplified.event = event.event;
    
    // Добавляем статусы согласий если есть
    if (event.analytics_storage) simplified.analytics_storage = event.analytics_storage;
    if (event.ad_storage) simplified.ad_storage = event.ad_storage;
    if (event.ad_user_data) simplified.ad_user_data = event.ad_user_data;
    if (event.ad_personalization) simplified.ad_personalization = event.ad_personalization;
    
    // Добавляем статусы из отдельного события consent_update
    if (event.consentStatus) simplified.consentStatus = event.consentStatus;
    if (event.analyticsConsent) simplified.analyticsConsent = event.analyticsConsent;
    if (event.adsConsent) simplified.adsConsent = event.adsConsent;
    
    // Добавляем gtm.start если есть (для инициализации GTM)
    if (event['gtm.start']) simplified['gtm.start'] = new Date(event['gtm.start']).toLocaleTimeString();
    
    return simplified;
  } catch (e) {
    return { type: 'error', error: e.message };
  }
}

/**
 * Simplified Auth Status Widget
 */
const AuthStatusWidget = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    tokenStatus: 'Checking...',
    tokenAge: null,
    tokenExpiryTime: null,
    timeToExpiry: null,
    errors: []
  });
  
  // Storage tracking
  const [storageInfo, setStorageInfo] = useState({
    cookies: {},
    localStorage: {}
  });

  // Consent tracking
  const [consentInfo, setConsentInfo] = useState({
    hasUserMadeChoice: false,
    preferences: {
      essential: true,
      analytics: false,
      advertising: false
    },
    dataLayer: []
  });

  const { 
    user, 
    isLoggedIn, 
    loading,
    error, 
    accessToken,
    refreshTokenIfNeeded,
    getFirebaseToken,
    profileData,
    purchases
  } = useUserStore();

  // Function to decode JWT token
  const decodeJwt = (token) => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error("Error decoding token:", e);
      return null;
    }
  };

  // Get authentication storage data
  const getStorageData = () => {
    const result = {
      cookies: {},
      localStorage: {}
    };
    
    // Get cookies
    const allCookies = document.cookie.split(';').reduce((cookies, cookie) => {
      const [name, value] = cookie.trim().split('=').map(c => c.trim());
      cookies[name] = value;
      return cookies;
    }, {});
    
    // Filter only auth-related cookies
    const authCookieNames = ['firebase_token', 'jwt_access_token', 'jwt_refresh_token', 'auth_token', 'id_token'];
    
    authCookieNames.forEach(name => {
      if (allCookies[name]) {
        result.cookies[name] = {
          value: allCookies[name].substring(0, 20) + '...',
          exists: true,
          length: allCookies[name].length
        };
      }
    });
    
    // Check localStorage
    try {
      const authStorageKeys = [
        'firebase_token_debug_time',
        'firebase:authUser',
        'jwt_access_token',
        'jwt_refresh_token',
        'cookie_consent_preferences' // Добавляем ключ согласий
      ];
      
      authStorageKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          result.localStorage[key] = {
            value: value.length > 40 ? value.substring(0, 40) + '...' : value,
            exists: true,
            length: value.length
          };
        }
      });
    } catch (e) {
      console.error("Error reading localStorage:", e);
    }
    
    return result;
  };
  
  // Check token status
  const checkTokenStatus = async () => {
    const newDebugInfo = { ...debugInfo, errors: [] };
    
    try {
      // Update storage info
      setStorageInfo(getStorageData());
      
      if (!isLoggedIn) {
        newDebugInfo.tokenStatus = 'Not logged in';
        setDebugInfo(newDebugInfo);
        return;
      }

      // Check if token exists
      if (!accessToken) {
        newDebugInfo.tokenStatus = 'No token';
        setDebugInfo(newDebugInfo);
        return;
      }
      
      // Decode token
      const decodedToken = decodeJwt(accessToken);
      
      if (!decodedToken) {
        newDebugInfo.tokenStatus = 'Invalid token format';
        setDebugInfo(newDebugInfo);
        return;
      }
      
      // Store token details
      newDebugInfo.tokenDetails = decodedToken;
      
      const expiryTime = decodedToken.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeToExpiry = expiryTime - currentTime;
      const tokenAgeMs = currentTime - (decodedToken.iat * 1000);
      
      newDebugInfo.tokenExpiryTime = new Date(expiryTime).toLocaleString();
      newDebugInfo.tokenAge = Math.floor(tokenAgeMs / 1000 / 60) + ' min';
      newDebugInfo.timeToExpiry = timeToExpiry > 0 ? 
        Math.floor(timeToExpiry / 1000 / 60) + ' min' : 
        'EXPIRED';
      
      if (timeToExpiry <= 0) {
        newDebugInfo.tokenStatus = 'EXPIRED';
      } else if (timeToExpiry < 5 * 60 * 1000) { // Less than 5 minutes
        newDebugInfo.tokenStatus = 'EXPIRING SOON';
      } else {
        newDebugInfo.tokenStatus = 'VALID';
      }
      
      // Check time
      newDebugInfo.lastChecked = new Date().toLocaleString();
      
    } catch (e) {
      newDebugInfo.errors.push(`Token check error: ${e.message}`);
    }
    
    setDebugInfo(newDebugInfo);
  };

  // Проверка статуса согласий
  const checkConsentStatus = () => {
    if (typeof window === 'undefined') return;
    
    // Получаем текущие настройки
    const userMadeChoice = hasUserMadeChoice();
    const currentPreferences = getPreferences();
    
    // Проверяем dataLayer
    let dataLayerEvents = [];
    try {
      if (typeof window !== 'undefined' && window.dataLayer) {
        // Получаем последние 5 событий из dataLayer
        dataLayerEvents = window.dataLayer
          .filter(item => typeof item === 'object')  // Фильтруем только объекты
          .slice(-5)                                 // Берем последние 5 событий
          .map(event => simplifyDataLayerEvent(event)); // Упрощаем для отображения
      }
    } catch (e) {
      console.error("Error processing dataLayer:", e);
      dataLayerEvents = [{ error: e.message }];
    }
    
    // Обновляем состояние
    setConsentInfo({
      hasUserMadeChoice: userMadeChoice,
      preferences: currentPreferences,
      dataLayer: dataLayerEvents
    });
  };

  // Check token status on mount and periodically
  useEffect(() => {
    // Initial check
    checkTokenStatus();
    checkConsentStatus();
    
    // Set up periodic checks
    const intervalId = setInterval(() => {
      checkTokenStatus();
      checkConsentStatus();
    }, 15000); // Every 15 seconds
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLoggedIn, accessToken]);

  // Force token refresh
  const handleForceRefresh = async () => {
    try {
      const newToken = await getFirebaseToken(true);
      setDebugInfo(prev => ({
        ...prev,
        lastRefreshAttempt: new Date().toLocaleString(),
        tokenStatus: newToken ? 'MANUALLY REFRESHED' : 'REFRESH FAILED'
      }));
      
      setTimeout(checkTokenStatus, 1000);
    } catch (e) {
      setDebugInfo(prev => ({
        ...prev,
        errors: [...prev.errors, `Refresh error: ${e.message}`]
      }));
    }
  };

  // Collapsed mini widget
  if (!isExpanded) {
    return (
      <div className="fixed bottom-2 right-2 z-50">
        <button 
          onClick={() => setIsExpanded(true)}
          className="bg-gray-800 text-white px-3 py-1 rounded-md text-sm shadow-lg flex items-center space-x-1 hover:bg-gray-700"
        >
          <span>Auth Status</span>
          {debugInfo.tokenStatus && (
            <div className={`ml-2 w-2 h-2 rounded-full ${
              debugInfo.tokenStatus === 'VALID' ? 'bg-green-400' : 
              debugInfo.tokenStatus === 'EXPIRED' ? 'bg-red-400' :
              debugInfo.tokenStatus === 'EXPIRING SOON' ? 'bg-yellow-400' :
              debugInfo.tokenStatus === 'MANUALLY REFRESHED' ? 'bg-blue-400' :
              'bg-gray-400'
            }`}></div>
          )}
          
          {/* Добавляем индикатор состояния согласий */}
          {consentInfo.hasUserMadeChoice && (
            <div className={`ml-1 w-2 h-2 rounded-full ${
              consentInfo.preferences.analytics ? 'bg-green-400' : 'bg-red-400'
            }`}></div>
          )}
        </button>
      </div>
    );
  }

  // Expanded widget
  return (
    <div className="fixed bottom-2 right-2 z-50 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm overflow-auto max-h-[90vh]">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-bold">Auth Status</h3>
        <button 
          onClick={() => setIsExpanded(false)}
          className="text-gray-600 hover:text-gray-900"
        >
          ✕
        </button>
      </div>
      
      <div className="flex gap-4">
        {/* Column 1: Basic Status */}
        <div className="w-48 border-r border-gray-200 pr-3">
          <h4 className="font-medium mb-2">Auth Status</h4>
          <div className="flex flex-col gap-1">
            <div>
              <span className="font-medium">Status:</span> 
              <span className={`ml-1 ${isLoggedIn ? 'text-green-600' : 'text-red-600'}`}>
                {isLoggedIn ? 'Logged In' : 'Logged Out'}
              </span>
            </div>
            
            <div>
              <span className="font-medium">Loading:</span>
              <span className="ml-1">{loading ? 'Yes' : 'No'}</span>
            </div>
          </div>
          
          {/* Token Status */}
          <div className="mt-3">
            <h4 className="font-medium mb-1">Token</h4>
            <div className="flex flex-col mb-1">
              <div className={`p-1 px-2 rounded text-xs font-medium mb-1 ${
                debugInfo.tokenStatus === 'VALID' ? 'bg-green-100 text-green-800' : 
                debugInfo.tokenStatus === 'EXPIRED' ? 'bg-red-100 text-red-800' :
                debugInfo.tokenStatus === 'EXPIRING SOON' ? 'bg-yellow-100 text-yellow-800' :
                debugInfo.tokenStatus === 'MANUALLY REFRESHED' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {debugInfo.tokenStatus}
              </div>
              {debugInfo.timeToExpiry && (
                <div className="text-xs text-gray-600">
                  {debugInfo.timeToExpiry === 'EXPIRED' 
                    ? 'Token expired' 
                    : `Expires: ${debugInfo.timeToExpiry}`}
                </div>
              )}
            </div>
            
            <div className="text-xs">
              <div><span className="font-medium">Age:</span> {debugInfo.tokenAge || 'N/A'}</div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="mt-3 pt-2 border-t border-gray-200">
            <div className="flex flex-col gap-2">
              <button
                onClick={checkTokenStatus}
                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
              >
                Refresh Status
              </button>
              <button
                onClick={handleForceRefresh}
                className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
              >
                Refresh Token
              </button>
            </div>
          </div>
        </div>
        
        {/* Column 2: Storage Info */}
        <div className="w-48 border-r border-gray-200 pr-3">
          <h4 className="font-medium mb-2">Storage</h4>
          
          {/* Cookies */}
          <details className="mb-2">
            <summary className="cursor-pointer text-xs font-medium hover:text-blue-600">
              Cookies ({Object.keys(storageInfo.cookies).length})
            </summary>
            <div className="mt-1 bg-gray-50 p-2 rounded text-xs">
              {Object.keys(storageInfo.cookies).length === 0 ? (
                <p className="text-gray-500">No auth cookies</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {Object.entries(storageInfo.cookies).map(([name, details]) => (
                        <tr key={name} className="border-b border-gray-100">
                          <td className="py-1 pr-2 text-xs">{name}</td>
                          <td className="py-1 text-xs">{details.length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </details>
          
          {/* LocalStorage */}
          <details>
            <summary className="cursor-pointer text-xs font-medium hover:text-blue-600">
              LocalStorage ({Object.keys(storageInfo.localStorage).length})
            </summary>
            <div className="mt-1 bg-gray-50 p-2 rounded text-xs">
              {Object.keys(storageInfo.localStorage).length === 0 ? (
                <p className="text-gray-500">No localStorage</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {Object.entries(storageInfo.localStorage).map(([key, details]) => (
                        <tr key={key} className="border-b border-gray-100">
                          <td className="py-1 pr-2 text-xs">{key}</td>
                          <td className="py-1 text-xs">{details.length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </details>
          
          {/* Errors */}
          {error && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <span className="text-red-600 font-medium text-xs">Error:</span>
              <p className="text-red-600 text-xs mt-1 break-words">{error}</p>
            </div>
          )}
        </div>
        
        {/* Column 3: User Data */}
        <div className="w-48 border-r border-gray-200 pr-3">
          <h4 className="font-medium mb-2">User Data</h4>
          
          {isLoggedIn && user ? (
            <div className="text-xs">
              <div className="mb-1"><span className="font-medium">Email:</span> {user?.email || 'N/A'}</div>
              <div className="mb-1"><span className="font-medium">UID:</span> {user?.uid || 'N/A'}</div>
              <div className="mb-1"><span className="font-medium">Name:</span> {user?.displayName || 'N/A'}</div>
              
              <div className="mt-2">
                <div className="mb-1"><span className="font-medium">Admin:</span> {profileData?.is_admin ? 'Yes' : 'No'}</div>
                <div className="mb-1"><span className="font-medium">Sub Status:</span> {profileData?.is_subscription_active ? 'Active' : 'Inactive'}</div>
                <div className="mb-1"><span className="font-medium">Sub Type:</span> {profileData?.subscription || 'N/A'}</div>
                <div className="mb-1"><span className="font-medium">Sub Energy:</span> {profileData?.subscription_energy || 0}</div>
                <div className="mb-1"><span className="font-medium">Perm Energy:</span> {profileData?.permanent_energy || 0}</div>
              </div>
              
              {user?.photoURL && (
                <div className="mt-2 flex items-center">
                  <span className="font-medium mr-2">Avatar:</span>
                  <img src={user.photoURL} alt="User avatar" className="w-6 h-6 rounded-full" />
                </div>
              )}
              
              {/* User Purchases */}
              {profileData?.purchases && profileData.purchases.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer font-medium hover:text-blue-600">
                    Purchases ({profileData.purchases.length})
                  </summary>
                  <ul className="list-disc list-inside mt-1 pl-2">
                    {profileData.purchases.slice(0, 5).map((purchase, idx) => (
                      <li key={idx} className="truncate">{typeof purchase === 'object' ? 
                        safeStringify(purchase) : purchase}</li>
                    ))}
                    {profileData.purchases.length > 5 && (
                      <li className="text-gray-500">...and {profileData.purchases.length - 5} more</li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-xs">User not logged in</p>
          )}
        </div>

        {/* Column 4: НОВАЯ КОЛОНКА - Cookie Consent */}
        <div className="w-48">
          <h4 className="font-medium mb-2">Cookie Consent</h4>
          
          <div className="text-xs">
            <div className="mb-1">
              <span className="font-medium">Choice made:</span> 
              <span className={`ml-1 ${consentInfo.hasUserMadeChoice ? 'text-green-600' : 'text-red-600'}`}>
                {consentInfo.hasUserMadeChoice ? 'Yes' : 'No'}
              </span>
            </div>
            
            <div className="mt-2">
              <h5 className="font-medium">Preferences:</h5>
              <div className="flex flex-col gap-1 mt-1">
                <div>
                  <span>Essential:</span> 
                  <span className={`ml-1 ${consentInfo.preferences.essential ? 'text-green-600' : 'text-red-600'}`}>
                    {consentInfo.preferences.essential ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div>
                  <span>Analytics:</span> 
                  <span className={`ml-1 ${consentInfo.preferences.analytics ? 'text-green-600' : 'text-red-600'}`}>
                    {consentInfo.preferences.analytics ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div>
                  <span>Advertising:</span> 
                  <span className={`ml-1 ${consentInfo.preferences.advertising ? 'text-green-600' : 'text-red-600'}`}>
                    {consentInfo.preferences.advertising ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="mt-2">
              <h5 className="font-medium">GTM Status:</h5>
              <div className="flex flex-col gap-1 mt-1">
                <div>
                  <span>dataLayer:</span> 
                  <span className={`ml-1 ${typeof window !== 'undefined' && window.dataLayer ? 'text-green-600' : 'text-red-600'}`}>
                    {typeof window !== 'undefined' && window.dataLayer ? 'Loaded' : 'Not loaded'}
                  </span>
                </div>
                <div>
                  <span>Events:</span> 
                  <span className="ml-1">
                    {typeof window !== 'undefined' && window.dataLayer ? window.dataLayer.length : '0'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* DataLayer events */}
            {consentInfo.dataLayer && consentInfo.dataLayer.length > 0 && (
              <div className="mt-2">
                <details>
                  <summary className="cursor-pointer text-xs font-medium hover:text-blue-600">
                    Last Events ({consentInfo.dataLayer.length})
                  </summary>
                  <div className="mt-1 bg-gray-50 p-2 rounded max-h-40 overflow-y-auto">
                    {consentInfo.dataLayer.map((event, index) => (
                      <div key={index} className="mb-2 pb-1 border-b border-gray-200">
                        <div className="font-medium">{event.event ? `Event: ${event.event}` : `Item ${index+1}`}</div>
                        {event.event === 'consent_updated' && (
                          <div className="text-blue-600">Consent Update!</div>
                        )}
                        <pre className="text-xs whitespace-pre-wrap overflow-x-auto">
                          {safeStringify(event)}
                        </pre>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
          </div>
          
          {/* Consent Test Buttons */}
          <div className="mt-3 pt-2 border-t border-gray-200">
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  acceptAll();
                  updateConsentState(true, true);
                  setTimeout(checkConsentStatus, 100);
                }}
                className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
              >
                Accept All
              </button>
              <button
                onClick={() => {
                  updatePreferences({
                    essential: true,
                    analytics: false,
                    advertising: false
                  });
                  updateConsentState(false, false);
                  setTimeout(checkConsentStatus, 100);
                }}
                className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
              >
                Reject All
              </button>
              <button
                onClick={() => {
                  updatePreferences({
                    essential: true,
                    analytics: true,
                    advertising: false
                  });
                  updateConsentState(true, false);
                  setTimeout(checkConsentStatus, 100);
                }}
                className="px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600"
              >
                Analytics Only
              </button>
              <button
                onClick={checkConsentStatus}
                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
              >
                Refresh Status
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default AuthStatusWidget;